import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Modal from '../components/Modal';
import axios from '../services/axios';
import { formatApiDate } from '../utils/date';

const ConfiguracionTarifario = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tarifario, setTarifario] = useState(null);
  const [activeTab, setActiveTab] = useState('conceptos');
  const [planPagoConfig, setPlanPagoConfig] = useState({
    interesPorcentaje: '',
    maxCuotas: '',
  });
  
  // Modales
  const [modal, setModal] = useState({ isOpen: false, type: 'info', title: '', message: '', onConfirm: null });
  const [editModal, setEditModal] = useState({ isOpen: false, item: null, type: '' });

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const tarifarioRes = await axios.get('/tarifario/activo');
      setTarifario(tarifarioRes.data);
      setPlanPagoConfig({
        interesPorcentaje: tarifarioRes.data?.plan_pago_interes_porcentaje ?? 0,
        maxCuotas: tarifarioRes.data?.plan_pago_max_cuotas ?? 5,
      });
    } catch (error) {
      console.error('Error al cargar datos:', error);
      setModal({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: 'No se pudieron cargar los datos del tarifario',
        onConfirm: () => setModal({ ...modal, isOpen: false })
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGuardarPlanPago = async () => {
    try {
      const interes = Number(planPagoConfig.interesPorcentaje);
      const maxCuotas = Number(planPagoConfig.maxCuotas);

      if (!Number.isFinite(interes) || interes < 0) {
        setModal({
          isOpen: true,
          type: 'warning',
          title: 'Interés inválido',
          message: 'Ingrese un interés válido mayor o igual a 0.',
          onConfirm: () => setModal({ ...modal, isOpen: false })
        });
        return;
      }

      if (!Number.isFinite(maxCuotas) || !Number.isInteger(maxCuotas) || maxCuotas < 1) {
        setModal({
          isOpen: true,
          type: 'warning',
          title: 'Cuotas inválidas',
          message: 'Ingrese una cantidad máxima de cuotas válida (entero mayor o igual a 1).',
          onConfirm: () => setModal({ ...modal, isOpen: false })
        });
        return;
      }

      await axios.put('/tarifario/plan-pago', {
        interesPorcentaje: interes,
        maxCuotas: maxCuotas,
      });

      setModal({
        isOpen: true,
        type: 'success',
        title: 'Plan de pago actualizado',
        message: 'Los parámetros del plan de pago se guardaron correctamente.',
        onConfirm: () => {
          setModal({ ...modal, isOpen: false });
          cargarDatos();
        }
      });
    } catch (error) {
      console.error('Error al guardar plan de pago:', error);
      setModal({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: 'No se pudo actualizar el plan de pago.',
        onConfirm: () => setModal({ ...modal, isOpen: false })
      });
    }
  };

  const handleEdit = (item, type) => {
    setEditModal({ isOpen: true, item: { ...item }, type });
  };

  const handleSaveEdit = async () => {
    try {
      const { item, type } = editModal;
      
      let endpoint = '';
      let payload = {};

      switch (type) {
        case 'concepto':
          endpoint = `/tarifario/concepto-fijo/${item.id}`;
          payload = { monto: parseFloat(item.monto) };
          break;
        case 'escala':
          endpoint = `/tarifario/escala-consumo/${item.id}`;
          payload = { precio: parseFloat(item.precio_por_m3) };
          break;
        default:
          return;
      }

      await axios.put(endpoint, payload);
      
      setModal({
        isOpen: true,
        type: 'success',
        title: '¡Actualizado!',
        message: 'Los valores se actualizaron correctamente',
        onConfirm: () => {
          setModal({ ...modal, isOpen: false });
          cargarDatos();
        }
      });
      
      setEditModal({ isOpen: false, item: null, type: '' });
    } catch (error) {
      console.error('Error al guardar:', error);
      setModal({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: 'No se pudo guardar la actualización',
        onConfirm: () => setModal({ ...modal, isOpen: false })
      });
    }
  };

  const formatMonto = (monto) => {
    return new Intl.NumberFormat('es-AR', { 
      style: 'currency', 
      currency: 'ARS',
      minimumFractionDigits: 0 
    }).format(monto);
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Cargando configuración...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      
      <div className="flex-1 overflow-auto">
        {/* Header */}
        <div className="bg-white border-b sticky top-0 z-10">
          <div className="px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Configuración del Tarifario</h1>
                <p className="text-gray-600 mt-1">
                  {tarifario?.nombre} - Vigente desde {formatApiDate(tarifario?.vigencia_desde)}
                </p>
              </div>
              <button
                onClick={() => navigate('/administrativo/clientes')}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <span className="material-symbols-outlined">arrow_back</span>
                Volver
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 px-8">
            <button
              onClick={() => setActiveTab('conceptos')}
              className={`px-4 py-3 font-medium transition-colors border-b-2 ${
                activeTab === 'conceptos'
                  ? 'text-blue-600 border-blue-600'
                  : 'text-gray-600 border-transparent hover:text-gray-900'
              }`}
            >
              Conceptos Fijos
            </button>
            <button
              onClick={() => setActiveTab('escalas')}
              className={`px-4 py-3 font-medium transition-colors border-b-2 ${
                activeTab === 'escalas'
                  ? 'text-blue-600 border-blue-600'
                  : 'text-gray-600 border-transparent hover:text-gray-900'
              }`}
            >
              Escalas de Consumo
            </button>
            <button
              onClick={() => setActiveTab('plan_pago')}
              className={`px-4 py-3 font-medium transition-colors border-b-2 ${
                activeTab === 'plan_pago'
                  ? 'text-blue-600 border-blue-600'
                  : 'text-gray-600 border-transparent hover:text-gray-900'
              }`}
            >
              Plan de Pago
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          {/* Conceptos Fijos */}
          {activeTab === 'conceptos' && (
            <div className="space-y-4">
              <div className="bg-white rounded-lg border overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Concepto
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tipo Cliente
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Condición
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Monto
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {tarifario?.conceptosFijos?.map((concepto) => (
                      <tr key={concepto.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">{concepto.nombre}</div>
                          <div className="text-sm text-gray-500">{concepto.descripcion}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900">{concepto.tipo_cliente}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900">
                            {concepto.condicion_umbral_m3 
                              ? `${concepto.condicion_umbral_m3 > 20 ? '>' : '≤'} ${concepto.condicion_umbral_m3}m³`
                              : '-'
                            }
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-lg font-semibold text-gray-900">
                            {formatMonto(concepto.monto)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => handleEdit(concepto, 'concepto')}
                            className="text-blue-600 hover:text-blue-800 font-medium"
                          >
                            Editar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Escalas de Consumo */}
          {activeTab === 'escalas' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Particulares */}
              <div className="bg-white rounded-lg border overflow-hidden">
                <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-blue-100 border-b">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <span className="material-symbols-outlined">person</span>
                    Particulares
                  </h3>
                </div>
                <div className="p-6 space-y-4">
                  {tarifario?.escalasConsumo
                    ?.filter(e => e.tipo_cliente === 'Particulares')
                    .sort((a, b) => a.orden - b.orden)
                    .map((escala) => (
                      <div key={escala.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <div className="font-medium text-gray-900">
                            {escala.desde_m3} - {escala.hasta_m3 || '∞'} m³
                          </div>
                          <div className="text-2xl font-bold text-blue-600 mt-1">
                            {formatMonto(escala.precio_por_m3)}/m³
                          </div>
                        </div>
                        <button
                          onClick={() => handleEdit(escala, 'escala')}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          Editar
                        </button>
                      </div>
                    ))}
                </div>
              </div>

              {/* Entidad Pública */}
              <div className="bg-white rounded-lg border overflow-hidden">
                <div className="px-6 py-4 bg-gradient-to-r from-green-50 to-green-100 border-b">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <span className="material-symbols-outlined">domain</span>
                    Entidad Pública
                  </h3>
                </div>
                <div className="p-6 space-y-4">
                  {tarifario?.escalasConsumo
                    ?.filter(e => e.tipo_cliente === 'Entidad Pública')
                    .sort((a, b) => a.orden - b.orden)
                    .map((escala) => (
                      <div key={escala.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <div className="font-medium text-gray-900">
                            {escala.desde_m3} - {escala.hasta_m3 || '∞'} m³
                          </div>
                          <div className="text-2xl font-bold text-green-600 mt-1">
                            {formatMonto(escala.precio_por_m3)}/m³
                          </div>
                        </div>
                        <button
                          onClick={() => handleEdit(escala, 'escala')}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                          Editar
                        </button>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'plan_pago' && (
            <div className="max-w-2xl">
              <div className="bg-white rounded-lg border p-6 space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Parámetros del plan de pago</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Defina el interés total y la cantidad máxima de cuotas para los planes de pago.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Interés total (%)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={planPagoConfig.interesPorcentaje}
                      onChange={(e) => setPlanPagoConfig({
                        ...planPagoConfig,
                        interesPorcentaje: e.target.value
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Ej: 10"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Máximo de cuotas
                    </label>
                    <input
                      type="number"
                      step="1"
                      min="1"
                      value={planPagoConfig.maxCuotas}
                      onChange={(e) => setPlanPagoConfig({
                        ...planPagoConfig,
                        maxCuotas: e.target.value
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Ej: 5"
                    />
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={handleGuardarPlanPago}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    Guardar configuración
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Modal de Edición */}
      {editModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              Editar {editModal.type === 'escala' ? 'Escala de Consumo' : 'Concepto Fijo'}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {editModal.type === 'escala' ? 'Precio por m³' : 'Monto'}
                </label>
                <input
                  type="number"
                  value={editModal.type === 'escala' ? editModal.item.precio_por_m3 : editModal.item.monto}
                  onChange={(e) => setEditModal({
                    ...editModal,
                    item: {
                      ...editModal.item,
                      [editModal.type === 'escala' ? 'precio_por_m3' : 'monto']: e.target.value
                    }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ingrese el monto"
                />
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setEditModal({ isOpen: false, item: null, type: '' })}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveEdit}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Guardar Cambios
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Mensajes */}
      <Modal
        isOpen={modal.isOpen}
        type={modal.type}
        title={modal.title}
        message={modal.message}
        onConfirm={modal.onConfirm}
        onCancel={() => setModal({ ...modal, isOpen: false })}
      />
    </div>
  );
};

export default ConfiguracionTarifario;
